/**
 * JITAccessRepository.ts - Just-In-Time Access Management Repository
 *
 * FERP Repository Pattern - Frontend data access layer for JIT operations
 *
 * GitHub Issue: #343
 *
 * FERP Principles:
 * - Repository Pattern: All JIT data access through this layer
 * - Data-Driven: All values from GraphQL backend
 * - Immutable Audit: All operations tracked
 * - Multi-Tenant: Complete tenant isolation
 * - Type-Safe: Full TypeScript type definitions
 */

import { ApolloClient, gql } from '@apollo/client';
import { BaseRepository } from './BaseRepository';

// ============================================================================
// Type Definitions (matching backend GraphQL schema)
// ============================================================================

/**
 * JIT Access Request
 *
 * Request for temporary role elevation
 */
export interface JITAccessRequest {
  requestId: string;
  tenantId: string;
  requesterId: string;
  requesterEmail: string;
  requesterRole: string;
  requestedRole: string;
  requestedRoleDisplayName?: string;
  durationMinutes: number;
  createdAt: string;
  expiresAt?: string;
  justification: string;
  emergency: boolean;
  status: string;
  approvedBy: string[];
  approvedAt?: string;
  rejectionReason?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  sessionId?: string;
  metadata: string;
}

/**
 * JIT Active Session
 *
 * Active temporary role session
 */
export interface JITActiveSession {
  sessionId: string;
  requestId: string;
  tenantId: string;
  userId: string;
  userEmail: string;
  roleId: string;
  roleName: string;
  roleDisplayName?: string;
  grantedAt: string;
  expiresAt: string;
  lastActivity: string;
  revokedAt?: string;
  revokedBy?: string;
  revocationReason?: string;
  extensionCount: number;
  originalDurationMinutes: number;
  remainingMinutes: number;
  status: string;
  metadata: string;
}

/**
 * JIT Policy
 *
 * Tenant-specific JIT access policy
 */
export interface JITPolicy {
  policyId: string;
  tenantId: string;
  allowedRoles: string[];
  defaultMaxDurationMinutes: number;
  autoApprovalEnabled: boolean;
  autoApprovalMaxDurationMinutes: number;
  autoApprovalRoles: string[];
  extensionEnabled: boolean;
  maxExtensionCount: number;
  emergencyAccessEnabled: boolean;
  enabled: boolean;
}

/**
 * JIT Statistics
 *
 * Analytics for tenant JIT usage
 */
export interface JITStatistics {
  tenantId: string;
  totalRequests: number;
  pendingRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  autoApprovedRequests: number;
  activeSessions: number;
  expiredSessions: number;
  revokedSessions: number;
  averageApprovalTimeMinutes: number;
  averageSessionDurationMinutes: number;
}

// ============================================================================
// Input Types (for mutations)
// ============================================================================

export interface RequestJITAccessInput {
  tenantId: string;
  requesterId: string;
  requesterEmail: string;
  requesterRole: string;
  requestedRole: string;
  durationMinutes: number;
  justification: string;
  emergency?: boolean;
}

export interface ApproveJITRequestInput {
  tenantId: string;
  requestId: string;
  approverId: string;
  approverEmail: string;
  durationMinutes?: number;
}

export interface RejectJITRequestInput {
  tenantId: string;
  requestId: string;
  rejectedBy: string;
  reason: string;
}

export interface ExtendJITSessionInput {
  tenantId: string;
  sessionId: string;
  additionalMinutes: number;
  justification: string;
}

export interface RevokeJITSessionInput {
  tenantId: string;
  sessionId: string;
  revokedBy: string;
  reason?: string;
}

export interface JITRequestFilter {
  tenantId: string;
  status?: string;
  requesterId?: string;
  limit?: number;
}

// ============================================================================
// Response Types
// ============================================================================

export interface RequestJITAccessResult {
  requestId: string;
  status: string;
  requiresApproval: boolean;
  sessionId?: string;
  expiresAt?: string;
  message: string;
}

export interface ApproveJITRequestResult {
  requestId: string;
  sessionId: string;
  expiresAt: string;
  message: string;
}

export interface RejectJITRequestResult {
  requestId: string;
  success: boolean;
  message: string;
}

export interface ExtendJITSessionResult {
  sessionId: string;
  newExpiresAt: string;
  message: string;
}

export interface RevokeJITSessionResult {
  sessionId: string;
  success: boolean;
  message: string;
}

export interface ListJITRequestsResult {
  requests: JITAccessRequest[];
  totalCount: number;
}

export interface ListActiveJITSessionsResult {
  sessions: JITActiveSession[];
  totalCount: number;
}

// ============================================================================
// GraphQL Operations
// ============================================================================

const REQUEST_JIT_ACCESS = gql`
  mutation RequestJITAccess($input: JITAccessRequestInput!) {
    requestJITAccess(input: $input) {
      requestId
      status
      requiresApproval
      sessionId
      expiresAt
      message
    }
  }
`;

const APPROVE_JIT_REQUEST = gql`
  mutation ApproveJITRequest($input: ApproveJITRequestInput!) {
    approveJITRequest(input: $input) {
      requestId
      sessionId
      expiresAt
      message
    }
  }
`;

const REJECT_JIT_REQUEST = gql`
  mutation RejectJITRequest($input: RejectJITRequestInput!) {
    rejectJITRequest(input: $input) {
      requestId
      success
      message
    }
  }
`;

const EXTEND_JIT_SESSION = gql`
  mutation ExtendJITSession($input: ExtendJITSessionInput!) {
    extendJITSession(input: $input) {
      sessionId
      newExpiresAt
      message
    }
  }
`;

const REVOKE_JIT_SESSION = gql`
  mutation RevokeJITSession($input: RevokeJITSessionInput!) {
    revokeJITSession(input: $input) {
      sessionId
      success
      message
    }
  }
`;

const LIST_JIT_REQUESTS = gql`
  query ListJITRequests($filter: JITRequestFilter!) {
    listJITRequests(filter: $filter) {
      requests {
        requestId
        tenantId
        requesterId
        requesterEmail
        requesterRole
        requestedRole
        requestedRoleDisplayName
        durationMinutes
        createdAt
        expiresAt
        justification
        emergency
        status
        approvedBy
        approvedAt
        rejectionReason
        rejectedBy
        rejectedAt
        sessionId
        metadata
      }
      totalCount
    }
  }
`;

const LIST_ACTIVE_JIT_SESSIONS = gql`
  query ListActiveJITSessions($tenantId: String!) {
    listActiveJITSessions(tenantId: $tenantId) {
      sessions {
        sessionId
        requestId
        tenantId
        userId
        userEmail
        roleId
        roleName
        roleDisplayName
        grantedAt
        expiresAt
        lastActivity
        revokedAt
        revokedBy
        revocationReason
        extensionCount
        originalDurationMinutes
        remainingMinutes
        status
        metadata
      }
      totalCount
    }
  }
`;

const GET_JIT_POLICY = gql`
  query GetJITPolicy($tenantId: String!) {
    getJITPolicy(tenantId: $tenantId) {
      policyId
      tenantId
      allowedRoles
      defaultMaxDurationMinutes
      autoApprovalEnabled
      autoApprovalMaxDurationMinutes
      autoApprovalRoles
      extensionEnabled
      maxExtensionCount
      emergencyAccessEnabled
      enabled
    }
  }
`;

const GET_JIT_STATISTICS = gql`
  query GetJITStatistics($tenantId: String!) {
    getJITStatistics(tenantId: $tenantId) {
      tenantId
      totalRequests
      pendingRequests
      approvedRequests
      rejectedRequests
      autoApprovedRequests
      activeSessions
      expiredSessions
      revokedSessions
      averageApprovalTimeMinutes
      averageSessionDurationMinutes
    }
  }
`;

// ============================================================================
// JITAccessRepository Class
// ============================================================================

/**
 * JITAccessRepository - Frontend JIT data access layer
 *
 * Extends BaseRepository for:
 * - Safety validation
 * - Audit trail integration
 * - Error handling
 * - Retry logic
 */
export class JITAccessRepository extends BaseRepository<JITAccessRequest> {
  private apolloClient: ApolloClient;

  constructor(
    client: ApolloClient,
    auditTrail?: any
  ) {
    super();
    this.apolloClient = client;
    if (auditTrail) {
      this.auditTrail = auditTrail;
    }
  }

  // ==========================================================================
  // Mutation Operations
  // ==========================================================================

  /**
   * Request JIT access to a role
   *
   * @param input - Request input
   * @returns Request result with approval status
   */
  async requestAccess(input: RequestJITAccessInput): Promise<RequestJITAccessResult> {
    await this.validateRepositorySafety({
      operation: 'REQUEST_JIT_ACCESS',
      domain: 'RBAC',
      tenantId: input.tenantId,
      entityType: 'JITAccessRequest',
      criticalityLevel: input.emergency ? 'HIGH' : 'MEDIUM',
    });

    const result = await this.apolloClient.mutate<Record<string, any>>({
      mutation: REQUEST_JIT_ACCESS,
      variables: { input },
    });

    return result.data?.requestJITAccess ?? null;
  }

  /**
   * Approve a JIT access request
   *
   * @param input - Approval input
   * @returns Approval result with session details
   */
  async approveRequest(input: ApproveJITRequestInput): Promise<ApproveJITRequestResult> {
    await this.validateRepositorySafety({
      operation: 'APPROVE_JIT_REQUEST',
      domain: 'RBAC',
      tenantId: input.tenantId,
      entityType: 'JITAccessRequest',
      criticalityLevel: 'HIGH',
    });

    const result = await this.apolloClient.mutate<Record<string, any>>({
      mutation: APPROVE_JIT_REQUEST,
      variables: { input },
    });

    return result.data?.approveJITRequest ?? null;
  }

  /**
   * Reject a JIT access request
   *
   * @param input - Rejection input
   * @returns Rejection result
   */
  async rejectRequest(input: RejectJITRequestInput): Promise<RejectJITRequestResult> {
    await this.validateRepositorySafety({
      operation: 'REJECT_JIT_REQUEST',
      domain: 'RBAC',
      tenantId: input.tenantId,
      entityType: 'JITAccessRequest',
      criticalityLevel: 'MEDIUM',
    });

    const result = await this.apolloClient.mutate<Record<string, any>>({
      mutation: REJECT_JIT_REQUEST,
      variables: { input },
    });

    return result.data?.rejectJITRequest ?? null;
  }

  /**
   * Extend an active JIT session
   *
   * @param input - Extension input
   * @returns Extension result with new expiration
   */
  async extendSession(input: ExtendJITSessionInput): Promise<ExtendJITSessionResult> {
    await this.validateRepositorySafety({
      operation: 'EXTEND_JIT_SESSION',
      domain: 'RBAC',
      tenantId: input.tenantId,
      entityType: 'JITActiveSession',
      criticalityLevel: 'MEDIUM',
    });

    const result = await this.apolloClient.mutate<Record<string, any>>({
      mutation: EXTEND_JIT_SESSION,
      variables: { input },
    });

    return result.data?.extendJITSession ?? null;
  }

  /**
   * Revoke an active JIT session
   *
   * @param input - Revocation input
   * @returns Revocation result
   */
  async revokeSession(input: RevokeJITSessionInput): Promise<RevokeJITSessionResult> {
    await this.validateRepositorySafety({
      operation: 'REVOKE_JIT_SESSION',
      domain: 'RBAC',
      tenantId: input.tenantId,
      entityType: 'JITActiveSession',
      criticalityLevel: 'HIGH',
    });

    const result = await this.apolloClient.mutate<Record<string, any>>({
      mutation: REVOKE_JIT_SESSION,
      variables: { input },
    });

    return result.data?.revokeJITSession ?? null;
  }

  // ==========================================================================
  // Query Operations
  // ==========================================================================

  /**
   * List JIT access requests (with optional filtering)
   *
   * @param filter - Request filter
   * @returns List of requests with count
   */
  async listRequests(filter: JITRequestFilter): Promise<ListJITRequestsResult> {
    await this.validateRepositorySafety({
      operation: 'LIST_JIT_REQUESTS',
      domain: 'RBAC',
      tenantId: filter.tenantId,
      entityType: 'JITAccessRequest',
      criticalityLevel: 'LOW',
    });

    const result = await this.apolloClient.query<Record<string, any>>({
      query: LIST_JIT_REQUESTS,
      variables: { filter },
      fetchPolicy: 'network-only', // Always fetch fresh data for real-time status
    });

    return result.data?.listJITRequests ?? null;
  }

  /**
   * List active JIT sessions
   *
   * @param tenantId - Tenant ID
   * @returns List of active sessions with countdown timers
   */
  async listActiveSessions(tenantId: string): Promise<ListActiveJITSessionsResult> {
    await this.validateRepositorySafety({
      operation: 'LIST_ACTIVE_JIT_SESSIONS',
      domain: 'RBAC',
      tenantId,
      entityType: 'JITActiveSession',
      criticalityLevel: 'LOW',
    });

    const result = await this.apolloClient.query<Record<string, any>>({
      query: LIST_ACTIVE_JIT_SESSIONS,
      variables: { tenantId },
      fetchPolicy: 'network-only', // Real-time data for countdown timers
    });

    return result.data?.listActiveJITSessions ?? null;
  }

  /**
   * Get JIT policy for tenant
   *
   * @param tenantId - Tenant ID
   * @returns JIT policy configuration
   */
  async getPolicy(tenantId: string): Promise<JITPolicy | null> {
    await this.validateRepositorySafety({
      operation: 'GET_JIT_POLICY',
      domain: 'RBAC',
      tenantId,
      entityType: 'JITPolicy',
      criticalityLevel: 'LOW',
    });

    const result = await this.apolloClient.query<Record<string, any>>({
      query: GET_JIT_POLICY,
      variables: { tenantId },
      fetchPolicy: 'cache-first', // Policy rarely changes
    });

    return result.data?.getJITPolicy ?? null;
  }

  /**
   * Get JIT statistics for tenant
   *
   * @param tenantId - Tenant ID
   * @returns JIT usage statistics
   */
  async getStatistics(tenantId: string): Promise<JITStatistics> {
    await this.validateRepositorySafety({
      operation: 'GET_JIT_STATISTICS',
      domain: 'RBAC',
      tenantId,
      entityType: 'JITStatistics',
      criticalityLevel: 'LOW',
    });

    const result = await this.apolloClient.query<Record<string, any>>({
      query: GET_JIT_STATISTICS,
      variables: { tenantId },
      fetchPolicy: 'network-only', // Real-time analytics
    });

    return result.data?.getJITStatistics ?? null;
  }
}
